// VOY-461 — Voy My Trip results page. Hosts CompoundResultsView, driven by
// useVoyMyTrip. Reachable from the search card's "Voy My Trip" button and the
// homepage hero CTA. Reads optional ?intent= and ?components= (csv) from the
// URL; otherwise assembles purely from memory (zero typed input).

import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useVoyMyTrip } from '@/hooks/useVoyMyTrip'
import { CompoundResultsView } from '@/components/search/CompoundResultsView'
import type { TripComponent } from '@/lib/search/refinement-constraints'

const VALID_COMPONENTS: TripComponent[] = ['flights', 'hotels', 'itinerary']

export default function VoyMyTripPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { session, changedTypes, build, refineUtterance, refinePills, swapAlternative } = useVoyMyTrip()
  const startedRef = useRef(false)

  const intent = params.get('intent')
  const componentsParam = params.get('components')
  const components = useMemo<TripComponent[] | undefined>(() => {
    if (!componentsParam) return undefined
    const list = componentsParam.split(',').map((s) => s.trim()).filter((c): c is TripComponent => (VALID_COMPONENTS as string[]).includes(c))
    return list.length ? list : undefined
  }, [componentsParam])

  // Kick off assembly once on mount.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    void build({ intent: intent ?? undefined, components })
  }, [build, intent, components])

  const hasResults = !!session.framing && Object.keys(session.components).length > 0

  return (
    <div className="min-h-screen px-4 py-10">
      {!hasResults && session.streaming && (
        <div className="vmt-results">
          <div className="mx-auto max-w-md text-center py-24">
            <div className="vmt-streaming" style={{ justifyContent: 'center' }}>
              <span className="vmt-streaming__dot" />
              Voy is assembling your trip from memory…
            </div>
          </div>
        </div>
      )}

      {!hasResults && !session.streaming && (
        <div className="mx-auto max-w-md text-center py-24 text-neutral-600">
          <p>{session.lastMessage ?? 'Could not assemble a trip. Try again from search.'}</p>
          <button className="vmt-btn vmt-btn--ghost mt-4" onClick={() => navigate('/')}>Back to search</button>
        </div>
      )}

      {hasResults && (
        <CompoundResultsView
          session={session}
          changedTypes={changedTypes}
          onSubmitUtterance={(u) => void refineUtterance(u)}
          onConstraintChange={(next) => void refinePills(next)}
          onSwapAlternative={swapAlternative}
          onSaveTrip={() => { /* VOY-462 TripPlan already persisted; save = no-op stub */ }}
          onBook={() => navigate('/checkout')}
        />
      )}
    </div>
  )
}
